import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import PlatformPosture from "@/components/landing/PlatformPosture";
import Strategies from "@/components/landing/Strategies";
import Partnership from "@/components/landing/Partnership";
import Perspectives from "@/components/landing/Perspectives";
import Footer from "@/components/landing/Footer";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";

import FundArchTeaser from "@/components/v2/FundArchTeaser";
import MidPageCTA from "@/components/v2/MidPageCTA";
import InstitutionalTrust from "@/components/v2/InstitutionalTrust";
import CloseInvestorAccess from "@/components/v2/CloseInvestorAccess";

/**
 * HomeV2 — tightened 9-section institutional flow.
 *
 *  01  Hero
 *  02  Platform Posture
 *  03  Strategies (4 mandates · the single "what we do" pillar)
 *  04  Fund Architecture (compact 9-vehicle strip → /funds)
 *  05  Partnership (Marcel · Chris · Rachel + structural pillars)
 *  --  Mid-page Investor Access CTA (conversion hook)
 *  06  Institutional Trust (Governance + Stewardship + Platform Infra — composite)
 *  07  Perspectives (authority signal)
 *  08  Close — Investor Access + Offices strip
 *
 * Mounted at /v2 as a non-destructive parallel route so the live homepage
 * is untouched while we evaluate flow.
 */
const HomeV2 = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultInterest, setDefaultInterest] = useState("investor");

  const openModal = useCallback((opts) => {
    if (opts && typeof opts === "object" && opts.interest) {
      setDefaultInterest(opts.interest);
    } else {
      setDefaultInterest("investor");
    }
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("kr-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" }
    );
    const els = document.querySelectorAll("section");
    els.forEach((el) => {
      el.classList.add("kr-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    el && el.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <>
      <Navbar onRequestAccess={() => openModal()} />

      {/* v2 mockup banner — only on /v2, dismiss-on-link to / */}
      <div
        data-testid="kr-v2-banner"
        className="fixed top-[80px] right-4 lg:right-8 z-40 border border-[var(--kr-gold)]/60 px-4 py-2.5 flex items-center gap-4 text-[10px] font-mono-label"
        style={{
          background: "rgba(10,13,18,0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <span className="text-[var(--kr-gold)]">v2 · Tightened Flow</span>
        <span className="w-6 h-px bg-[var(--kr-gold)]/40" />
        <Link
          to="/"
          data-testid="kr-v2-banner-back"
          className="text-[var(--kr-text-muted)] hover:text-[var(--kr-gold)] transition-colors"
        >
          ← Compare v1
        </Link>
      </div>

      <main className="relative">
        <Hero
          onRequestAccess={() => openModal()}
          onExplore={() => scrollTo("strategies")}
        />
        <PlatformPosture />
        <Strategies />
        <FundArchTeaser />
        <Partnership />
        <MidPageCTA onRequestAccess={() => openModal()} />
        <InstitutionalTrust />
        <Perspectives />
        <CloseInvestorAccess
          onRequestAccess={() => openModal()}
          onStrategic={() => openModal({ interest: "strategic" })}
        />
      </main>
      <Footer />

      <InvestorAccessModal
        open={modalOpen}
        onClose={closeModal}
        defaultInterest={defaultInterest}
      />
    </>
  );
};

export default HomeV2;
